import { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from '@/styles/superstack/base/Checkbox.module.scss';
import PropTypes from 'prop-types';
import CheckedBoxLG from '@/asset/superstack/svgs/icon-checkbox-checked.svg';
import CheckedBoxSM from '@/asset/superstack/svgs/icon-checkbox-checked-sm.svg';
import IconCheck from '@/asset/superstack/svgs/icon-check.svg';

const Checkbox = ({ size, name, value, checked, disabled, updateContainer, isLabelInline=false, ...props }) => {
  const cx = classNames.bind(styles);
  const cns = disabled ? [`label--${size}`, 'label--disabled'] : [`label--${size}`];
  if (isLabelInline){
    cns.push('label-inline');
  }

  const [isChecked, setIsChecked] = useState(checked || false);
  const iconCheckedBox =
    size === 'lg' ? (
      <CheckedBoxLG width={24} height={24} />
    ) : (
      <CheckedBoxSM width={18} height={18} />
    );
  const inputDisplayed = isChecked ? (
    <span className={cx('input-display')}>{iconCheckedBox}</span>
  ) : (
    <span className={cx(['input-display', 'checkbox'])} />
  );

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    if (props.isReset) {
      setIsChecked(false);
    }
  }, [props.isReset]);

  const handleChecked = (e) => {
    setIsChecked(!isChecked);

    if (updateContainer) {
      updateContainer(e);
    }
  };

  return (
    <label className={cx(cns)}>
      <input
        type="checkbox"
        name={name}
        value={value}
        className={cx('input')}
        onChange={handleChecked}
        checked={isChecked}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      {inputDisplayed}
      {props.children}
    </label>
  );
};

const ChipCheckbox = ({ size, name, value, checked, disabled, updateContainer, ...props }) => {
  const cx = classNames.bind(styles);
  const [isChecked, setIsChecked] = useState(checked || false);
  const cns = [`chip--${isChecked ? 'filled' : 'text'}`, `chip--${size}`];
  const iconCheckedBox = <IconCheck width={14} height={14} fill={'#006DFF'} />;
  const inputDisplayed = isChecked ? (
    <span className={cx('input-display')}>{iconCheckedBox}</span>
  ) : (
    <></>
  );

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    if (props.isReset) {
      setIsChecked(false);
    }
  }, [props.isReset]);

  const handleChecked = (e) => {
    setIsChecked((isChecked) => {
      return !isChecked;
    });

    if (updateContainer) {
      updateContainer(e);
    }
  };

  return (
    <label className={cx(cns)}>
      <input
        type="checkbox"
        name={name}
        value={value}
        className={cx('input')}
        onChange={handleChecked}
        checked={isChecked}
        disabled={disabled}
      />
      {inputDisplayed}
      {props.children}
    </label>
  );
};

const ChipCheckboxFixed = ({ size, name, value, checked, disabled, updateContainer, ...props }) => {
  const cx = classNames.bind(styles);
  const [isChecked, setIsChecked] = useState(checked || false);
  const cns = ['chip-fixed', `chip--${isChecked ? 'filled' : 'text'}`, `chip--${size}`];
  const iconChecked = <IconCheck width={18} height={18} fill={'#006DFF'} />;
  const iconUnChecked = <IconCheck width={18} height={18} fill={'#E0E0E0'} />;
  const inputDisplayed = (
    <span className={cx('input-display')}>{isChecked ? iconChecked : iconUnChecked}</span>
  );

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    if (props.isReset) {
      setIsChecked(false);
    }
  }, [props.isReset]);

  const handleChecked = (e) => {
    setIsChecked((isChecked) => {
      return !isChecked;
    });

    if (updateContainer) {
      updateContainer(e);
    }
  };

  return (
    <label className={cx(cns)}>
      <input
        type="checkbox"
        name={name}
        value={value}
        className={cx('input')}
        onChange={handleChecked}
        checked={isChecked}
        disabled={disabled}
      />
      {inputDisplayed}
      {props.children}
    </label>
  );
};

Checkbox.prototype = {
  size: PropTypes.string,
  name: PropTypes.string,
  checked: PropTypes.string,
  disabled: PropTypes.string,
};
Checkbox.defaultProps = {
  size: 'lg',
  name: 'test',
  checked: false,
  disabled: false,
};

ChipCheckbox.prototype = {
  size: PropTypes.string,
  name: PropTypes.string,
  checked: PropTypes.string,
  disabled: PropTypes.string,
};
ChipCheckbox.defaultProps = {
  size: 'xl',
  name: 'test',
  checked: false,
  disabled: false,
};

ChipCheckboxFixed.prototype = {
  size: PropTypes.string,
  name: PropTypes.string,
  checked: PropTypes.string,
  disabled: PropTypes.string,
};
ChipCheckboxFixed.defaultProps = {
  size: 'xxl',
  name: 'test',
  checked: false,
  disabled: false,
};

export { Checkbox, ChipCheckbox, ChipCheckboxFixed };
